import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Disclosure } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { optionName, type ImportLineDraft } from '../manualImportMeta';
import type { ManualImportForm } from '../useManualImport';
import { LabeledSelect } from './LabeledSelect';
import { ReadOnlyField } from './ReadOnlyField';
import { Icon } from '@iconify/react';

type LineVariationFieldsProps = {
    form: ManualImportForm;
    line: ImportLineDraft;
};

/**
 * The optional product-attribute (variation) fields for a line, scoped to the
 * line's selected category and tucked behind a collapsed Disclosure — they're
 * secondary to the identity/pricing fields, which all render above. New lines
 * get one value select per axis; existing lines show the picked variant's
 * chosen values read-only. Renders nothing when the line has no category, the
 * category declares no variation attributes, or (existing) the variant carries
 * no values.
 */
export const LineVariationFields: React.FC<LineVariationFieldsProps> = ({
    form,
    line,
}) => {
    const { t } = useTranslation('pages');

    const axes = line.categoryId
        ? (form.variationAxesByCategory[line.categoryId] ?? [])
        : [];

    if (axes.length === 0) {
        return null;
    }

    const chosen = axes
        .map((axis) => ({
            axis,
            valueId: line.optionValues[axis.attributeTypeId] ?? '',
        }))
        .filter((entry) => entry.valueId !== '');

    if (line.mode === 'existing' && chosen.length === 0) {
        return null;
    }

    return (
        <Disclosure
            {...getNodeTestIdProp(`manual-import-line-variation-${line.key}`)}
        >
            <Disclosure.Heading>
                <Button
                    slot="trigger"
                    variant="secondary"
                    {...getNodeTestIdProp(
                        `manual-import-line-variation-trigger-${line.key}`,
                    )}
                >
                    <Icon icon="solar:tag-bold-duotone" />
                    {t('purchases.manualImport.attributes')}
                    <Disclosure.Indicator />
                </Button>
            </Disclosure.Heading>
            <Disclosure.Content
                {...getNodeTestIdProp(
                    `manual-import-line-variation-content-${line.key}`,
                )}
            >
                <Disclosure.Body>
                    <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2 lg:grid-cols-4">
                        {line.mode === 'existing'
                            ? chosen.map(({ axis, valueId }) => (
                                  <ReadOnlyField
                                      key={axis.attributeTypeId}
                                      label={axis.name}
                                      slot={`line-variation-${line.key}-${axis.attributeTypeId}`}
                                      value={optionName(axis.values, valueId)}
                                  />
                              ))
                            : axes.map((axis) => (
                                  <LabeledSelect
                                      key={axis.attributeTypeId}
                                      label={axis.name}
                                      onChange={(value) =>
                                          form.onLineOptionChange(
                                              line.key,
                                              axis.attributeTypeId,
                                              value,
                                          )
                                      }
                                      options={axis.values}
                                      placeholder={t(
                                          'purchases.manualImport.variationPlaceholder',
                                      )}
                                      slot={`line-variation-${line.key}-${axis.attributeTypeId}`}
                                      value={
                                          line.optionValues[
                                              axis.attributeTypeId
                                          ] ?? ''
                                      }
                                  />
                              ))}
                    </div>
                </Disclosure.Body>
            </Disclosure.Content>
        </Disclosure>
    );
};
